import React, { useEffect, useState } from 'react';
import { Line, LineChart, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { Measurement } from '../../domain/Measurement';
import { computeStaleThreshold } from '../../domain/signal';
import './MeasurementChart.css';

// Forma mínima para sombrear un período sobre el gráfico — la cumplen tanto
// AlertEvent (episodios de alerta) como IrrigationEvent (riegos), así este
// componente sirve para superponer cualquiera de los dos sin acoplarse a uno.
export interface ChartEvent {
  id: string;
  startedAt: string;
  endedAt: string | null;
}

interface Props {
  measurements: Measurement[];
  unit: '%' | '°C';
  events?: ChartEvent[];
  eventColor?: string;
}

interface ChartRow {
  ts: number;
  value: number | null;
  gapValue: number | null;
}

const formatTime = (ts: number) =>
  new Date(ts).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });

function useIsNarrow(): boolean {
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 400);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 400);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isNarrow;
}

// Arma las filas del gráfico y, cuando el hueco entre dos lecturas (o entre
// la última lectura y "ahora") supera el umbral esperado, corta la línea
// sólida y agrega un tramo punteado que marca la pérdida de señal.
function buildRows(
  measurements: Measurement[],
  threshold: number,
  now: number
): { rows: ChartRow[]; gapRanges: [number, number][] } {
  const rows: ChartRow[] = [];
  const gapRanges: [number, number][] = [];

  // Si el dispositivo lleva mucho tiempo sin reportar, estirar el eje hasta
  // "ahora" tal cual comprimiría todo el historial real contra el borde
  // izquierdo. Se acota cuánto se extiende el tramo punteado final.
  const firstTs = new Date(measurements[0].recordedAt).getTime();
  const lastTs = new Date(measurements[measurements.length - 1].recordedAt).getTime();
  const span = Math.max(lastTs - firstTs, threshold);
  const maxTrailingExtension = Math.max(threshold * 3, span * 0.25);
  const cappedNow = Math.min(now, lastTs + maxTrailingExtension);

  measurements.forEach((m, i) => {
    const ts = new Date(m.recordedAt).getTime();
    rows.push({ ts, value: m.value, gapValue: null });

    const next = measurements[i + 1];
    const nextTs = next ? new Date(next.recordedAt).getTime() : cappedNow;
    if (nextTs - ts <= threshold) return;

    rows.push({ ts: ts + 1, value: null, gapValue: m.value });
    if (next) {
      rows.push({ ts: nextTs - 1, value: null, gapValue: next.value });
    } else {
      // Todavía no volvió a reportar: el punteado llega hasta "ahora" (acotado
      // para no aplastar el resto del historial), sostenido en el último valor conocido.
      rows.push({ ts: cappedNow, value: null, gapValue: m.value });
    }
    gapRanges.push([ts, nextTs]);
  });

  return { rows, gapRanges };
}

// Recorta el episodio de alerta a la ventana de tiempo visible en el gráfico.
// Devuelve null si no se superpone con lo que se está mostrando.
function eventTsRange(minTs: number, maxTs: number, event: ChartEvent): [number, number] | null {
  const start = new Date(event.startedAt).getTime();
  const end = event.endedAt ? new Date(event.endedAt).getTime() : maxTs;
  if (end < minTs || start > maxTs) return null;

  const clampedStart = Math.max(start, minTs);
  const clampedEnd = Math.min(end, maxTs);
  if (clampedEnd > clampedStart) return [clampedStart, clampedEnd];

  // Un episodio puntual colapsa a un instante: se ensancha un poco para que
  // la franja se siga viendo en vez de desaparecer como una línea sin ancho.
  const pad = Math.max((maxTs - minTs) * 0.01, 1000);
  return [Math.max(minTs, clampedStart - pad), Math.min(maxTs, clampedEnd + pad)];
}

const MeasurementChart: React.FC<Props> = ({ measurements, unit, events = [], eventColor = 'var(--domiot-danger)' }) => {
  const isNarrow = useIsNarrow();
  const color = unit === '%' ? 'var(--domiot-accent)' : 'var(--domiot-warning)';

  if (measurements.length < 2) {
    return (
      <p style={{ color: 'var(--domiot-text-muted)', fontSize: 13 }}>
        Hacen falta al menos dos mediciones para dibujar el gráfico.
      </p>
    );
  }

  const now = Date.now();
  const threshold = computeStaleThreshold(measurements.map((m) => m.recordedAt));
  const { rows, gapRanges } = buildRows(measurements, threshold, now);

  const minTs = rows[0].ts;
  const maxTs = rows[rows.length - 1].ts;
  const tickCount = isNarrow ? 3 : 5;
  const ticks =
    tickCount <= 1 || maxTs <= minTs
      ? [minTs]
      : Array.from({ length: tickCount }, (_, i) => Math.round(minTs + ((maxTs - minTs) * i) / (tickCount - 1)));

  const bands = events
    .map((event) => ({ event, range: eventTsRange(minTs, maxTs, event) }))
    .filter((b): b is { event: ChartEvent; range: [number, number] } => b.range !== null);

  return (
    <>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="ts"
            type="number"
            domain={[minTs, maxTs]}
            ticks={ticks}
            tickFormatter={formatTime}
            tick={{ fontSize: isNarrow ? 10 : 11, fill: 'var(--domiot-text-muted)' }}
            axisLine={{ stroke: 'var(--domiot-border)' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: isNarrow ? 10 : 11, fill: 'var(--domiot-text-muted)' }}
            axisLine={false}
            tickLine={false}
            width={isNarrow ? 34 : 42}
            unit={unit}
          />
          <Tooltip
            labelFormatter={(label) => formatTime(Number(label))}
            contentStyle={{
              background: 'var(--domiot-surface)',
              border: '1px solid var(--domiot-border)',
              borderRadius: 10,
              fontSize: 12,
            }}
            formatter={(value, name) =>
              name === 'gapValue' ? ['Sin señal', 'Estado'] : [`${value}${unit}`, 'Valor']
            }
          />
          {bands.map(({ event, range }) => (
            <ReferenceArea
              key={event.id}
              x1={range[0]}
              x2={range[1]}
              fill={eventColor}
              fillOpacity={0.12}
              stroke={eventColor}
              strokeOpacity={0.4}
              strokeDasharray="3 3"
              ifOverflow="extendDomain"
            />
          ))}
          {gapRanges.map(([x1, x2]) => (
            <ReferenceArea
              key={`gap-${x1}`}
              x1={x1}
              x2={x2}
              fill="var(--domiot-offline)"
              fillOpacity={0.14}
              stroke="var(--domiot-offline-border)"
              strokeOpacity={0.6}
              strokeDasharray="2 3"
              ifOverflow="extendDomain"
            />
          ))}
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          <Line
            type="monotone"
            dataKey="gapValue"
            stroke="var(--domiot-offline)"
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      {gapRanges.length > 0 && (
        <p className="measurement-chart-gap-note">
          <span className="measurement-chart-gap-swatch" />
          Tramos sin datos: se perdió señal de la estación
        </p>
      )}
    </>
  );
};

export default MeasurementChart;
